import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { createContext } from 'react';

import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { openPositionsBunja } from '@flipster/socket/r230522/position';
import { pswapDetailsBunja } from '@flipster/socket/r230522/pswap-detail';
import { pswapsBunja } from '@flipster/socket/r230522/pswaps';
import { tradeSymbolAtom } from '@flipster/socket/r230522/trade-symbol';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { calcEstLiquidationPrice, calcQtyToMargin } from '@flipster/utils/calculator/order-preview';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import Num, { BigNumber, countDecimalPlaces } from '@flipster/utils/num';

import { maxPositionLimit } from '@web/constants/position';

import { hedgingOrderLeverage, hedgingOrderSide } from '../constants';

export interface HedgingFormContext {
  stakingCurrency: string;
  stakedAmount: string;
}

export const HedgingFormContext = createContext<HedgingFormContext>(null!);
export const HedgingFormScope = createScopeFromContext(HedgingFormContext);

export const hedgingFormBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { stakingCurrency, stakedAmount } = bunja.use(HedgingFormScope);
  const { snapshotSpecAtom } = bunja.use(snapshotSpecBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pswapsAtom } = bunja.use(pswapsBunja);
  const { pswapDetailsAtom, setCurrentSymbol } = bunja.use(pswapDetailsBunja);

  const pswapInstrumentsAtom = atom((get) => get(snapshotSpecAtom)?.pswapInstruments);

  const symbolInfoAtom = atom((get) =>
    get(pswapInstrumentsAtom)?.find((pi) => pi.quoteCurrency === UsdtCurrency && pi.currency === stakingCurrency),
  );

  const pswapDetailAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return null;
    return get(pswapDetailsAtom)?.[symbolInfo.symbol];
  });

  const shortOpenPositionsAtom = atom((get) => {
    const openPositions = get(openPositionsAtom);
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    const { symbol } = symbolInfo;
    const allShortPositions = openPositions?.[symbol]?.filter((op) => op.side === 'Short') ?? [];
    return (
      allShortPositions
        ?.reduce((acc, curr) => acc.plus(Num(curr.position).abs()), Num(0))
        .abs()
        .toString() ?? '0'
    );
  });

  const orderPriceAtom = atom((get) => {
    const shortOpenPositions = get(shortOpenPositionsAtom);
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    const { unitOrderQty } = symbolInfo;
    return Num(stakedAmount)
      .minus(shortOpenPositions)
      .toFixed(countDecimalPlaces(unitOrderQty), BigNumber.ROUND_HALF_UP)
      .toString();
  });

  const marginAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    const pswapDetail = get(pswapDetailsAtom)?.[symbolInfo?.symbol];
    return calcQtyToMargin(get(orderPriceAtom), hedgingOrderLeverage, pswapDetail?.askPrice);
  });

  const liqShortPriceAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    const { maintMarginRate, tickSize } = symbolInfo;
    const pswapDetail = get(pswapDetailsAtom)?.[symbolInfo?.symbol];
    return calcEstLiquidationPrice(
      hedgingOrderSide,
      hedgingOrderLeverage,
      pswapDetail?.bidPrice,
      maintMarginRate,
      tickSize,
    );
  });

  const takerFeeRateAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return '0';
    return get(pswapsAtom)?.[symbolInfo.symbol]?.takerFeeRate ?? '0';
  });

  const isHedgeOverStakingAmountAtom = atom((get) => {
    return Num(get(orderPriceAtom)).isLessThanOrEqualTo(0);
  });

  const isOverMaxPositionsAtom = atom((get) => {
    const symbolInfo = get(symbolInfoAtom);
    if (!symbolInfo) return false;
    const { symbol } = symbolInfo;
    const openPositions = get(openPositionsAtom);
    return openPositions?.[symbol] && openPositions[symbol].length >= maxPositionLimit;
  });

  bunja.effect(() => {
    const symbolInfo = store.get(symbolInfoAtom);
    if (!symbolInfo) return;
    setCurrentSymbol(symbolInfo.symbol);
    store.set(tradeSymbolAtom, symbolInfo.symbol);
    return () => {
      setCurrentSymbol(null);
      store.set(tradeSymbolAtom, null);
    };
  });

  return {
    symbolInfoAtom,
    pswapDetailAtom,
    shortOpenPositionsAtom,
    orderPriceAtom,
    marginAtom,
    liqShortPriceAtom,
    takerFeeRateAtom,
    isHedgeOverStakingAmountAtom,
    isOverMaxPositionsAtom,
  };
});
